import type {
	AgentcTemplateListItem,
	AgentStatus,
	AgentTemplateOverride,
	GetAgentcTemplateContentResponse,
} from "@repo/common";
import {
	getAgentInboundAnsweringPolicy,
	type InboundAnsweringPolicy,
} from "@repo/common";
import {
	db,
	deleteAgent,
	deleteTemplateOverride,
	getAgentcTemplateContent,
	listAgentcTemplates,
	listTemplateOverrides,
	updateAgent,
	upsertTemplateOverride,
	useAuth,
} from "@repo/ui";
import { ArrowLeft, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { AdminCallStats } from "./AdminCallStats";
import { OutboundPlaybooksPanel } from "./OutboundPlaybooksPage";
import { TypedDeleteConfirmationDialog } from "./TypedDeleteConfirmationDialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Textarea } from "./ui/textarea";

interface AgentDetailsPageProps {
	agentId: string;
	onNavigateBack: () => void;
}

export function AgentDetailsPage({
	agentId,
	onNavigateBack,
}: AgentDetailsPageProps) {
	const { user } = useAuth();
	const { agents, companies, services, phoneNumbers, tcdrs, dailyCallStats } =
		useData();

	const agentStats = useMemo(
		() => dailyCallStats.filter((s) => s.agentId === agentId),
		[dailyCallStats, agentId],
	);

	const agent = useMemo(
		() => agents.find((a) => a.id === agentId),
		[agents, agentId],
	);

	const company = useMemo(
		() => (agent ? companies.find((c) => c.id === agent.companyId) : null),
		[companies, agent],
	);

	const service = useMemo(
		() =>
			agent?.serviceId ? services.find((s) => s.id === agent.serviceId) : null,
		[services, agent],
	);

	const agentPhoneNumbers = useMemo(
		() => phoneNumbers.filter((p) => p.agentId === agentId),
		[phoneNumbers, agentId],
	);

	const recentCalls = useMemo(() => {
		if (!agent) return [];
		return tcdrs
			.filter((tcdr) =>
				agent.phoneNumbers.some(
					(number) => tcdr.to === number || tcdr.from === number,
				),
			)
			.slice(0, 10);
	}, [tcdrs, agent]);

	const [isEditing, setIsEditing] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [formData, setFormData] = useState({
		companyId: agent?.companyId || "",
		serviceId: agent?.serviceId || "none",
		status: agent?.status || ("Draft" as AgentStatus),
		inboundAnsweringPolicy: getAgentInboundAnsweringPolicy(agent),
	});
	const [availableTemplates, setAvailableTemplates] = useState<
		AgentcTemplateListItem[]
	>([]);
	const [existingOverrides, setExistingOverrides] = useState<
		AgentTemplateOverride[]
	>([]);
	const [selectedTemplatePath, setSelectedTemplatePath] = useState<string>("");
	const [selectedTemplate, setSelectedTemplate] =
		useState<AgentcTemplateListItem | null>(null);
	const [templateContent, setTemplateContent] = useState("");
	const [templateMeta, setTemplateMeta] = useState<Pick<
		GetAgentcTemplateContentResponse,
		"cached" | "fetchedAt" | "expiresAt"
	> | null>(null);
	const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
	const [isLoadingContent, setIsLoadingContent] = useState(false);
	const [isSavingOverride, setIsSavingOverride] = useState(false);
	const [deletingOverrideId, setDeletingOverrideId] = useState<string | null>(
		null,
	);
	const [activeOverrideId, setActiveOverrideId] = useState<string | null>(null);
	const [activeTemplateTab, setActiveTemplateTab] = useState("editor");

	const loadOverrides = useCallback(async () => {
		if (!agent) return;
		const overrides = await listTemplateOverrides(
			db,
			agent.companyId,
			agent.id || "",
		);
		setExistingOverrides(
			overrides.sort((a, b) => a.templatePath.localeCompare(b.templatePath)),
		);
	}, [agent]);

	const loadTemplateCatalog = useCallback(async () => {
		setIsLoadingTemplates(true);
		try {
			const result = await listAgentcTemplates({});
			setAvailableTemplates(result.data.templates);
		} catch (error) {
			console.error(error);
			toast.error("Failed to load AgentC templates.");
		} finally {
			setIsLoadingTemplates(false);
		}
	}, []);

	useEffect(() => {
		setFormData({
			companyId: agent?.companyId || "",
			serviceId: agent?.serviceId || "none",
			status: agent?.status || ("Draft" as AgentStatus),
			inboundAnsweringPolicy: getAgentInboundAnsweringPolicy(agent),
		});
	}, [agent]);

	useEffect(() => {
		void loadTemplateCatalog();
	}, [loadTemplateCatalog]);

	useEffect(() => {
		if (!agent) return;
		void loadOverrides();
	}, [agent, loadOverrides]);

	if (!agent) {
		return (
			<div className="space-y-6">
				<div className="flex items-center gap-4">
					<Button variant="ghost" onClick={onNavigateBack}>
						<ArrowLeft className="mr-2 h-4 w-4" />
						Back to Agents
					</Button>
				</div>
				<Card>
					<CardContent className="py-8">
						<p className="text-center text-slate-500">Agent not found</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	const handleSave = async () => {
		if (!formData.companyId) {
			toast.error("Please fill in all required fields");
			return;
		}
		try {
			await updateAgent(db, agent.companyId, agent.id, {
				...formData,
				serviceId:
					formData.serviceId === "none" ? null : formData.serviceId || null,
				status: formData.status === "Paused" ? "Active" : formData.status,
			});
			toast.success(`Agent "${agent.id}" updated successfully`);
			setIsEditing(false);
		} catch (error) {
			console.error("Failed to update agent", error);
			toast.error("Failed to update agent");
		}
	};

	const handleCancel = () => {
		setFormData({
			companyId: agent.companyId,
			serviceId: agent.serviceId || "none",
			status: agent.status,
			inboundAnsweringPolicy: getAgentInboundAnsweringPolicy(agent),
		});
		setIsEditing(false);
	};

	const handleDeleteAgent = async () => {
		setIsDeleting(true);
		try {
			await deleteAgent(db, agent.companyId, agent.id);
			toast.success(`Agent "${agent.name}" deleted successfully`);
			onNavigateBack();
		} catch (error) {
			console.error("Failed to delete agent", error);
			toast.error("Failed to delete agent");
		} finally {
			setIsDeleting(false);
			setIsDeleteDialogOpen(false);
		}
	};

	const formatTimestamp = (timestamp: string | null) => {
		if (!timestamp) return "Never";
		return new Date(timestamp).toLocaleString();
	};

	const formatDuration = (duration: string) => {
		const seconds = Number.parseInt(duration, 10);
		if (seconds < 60) return `${seconds}s`;
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}m ${remainingSeconds}s`;
	};

	const getStatusBadge = (status: string) => {
		switch (status) {
			case "Active":
				return <Badge className="bg-green-100 text-green-800">Active</Badge>;
			case "Draft":
				return <Badge variant="outline">Draft</Badge>;
			default:
				return <Badge variant="outline">{status}</Badge>;
		}
	};

	const getCallStatusBadge = (status: string) => {
		switch (status) {
			case "completed":
				return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
			case "busy":
				return <Badge className="bg-yellow-100 text-yellow-800">Busy</Badge>;
			case "no-answer":
				return (
					<Badge className="bg-orange-100 text-orange-800">No Answer</Badge>
				);
			case "failed":
				return <Badge className="bg-red-100 text-red-800">Failed</Badge>;
			default:
				return <Badge variant="outline">{status}</Badge>;
		}
	};

	const handleLoadTemplate = async (templatePathOverride?: string) => {
		const templatePath = templatePathOverride ?? selectedTemplatePath;
		if (!templatePath) {
			toast.error("Select a template file first.");
			return;
		}
		setIsLoadingContent(true);
		try {
			const result = await getAgentcTemplateContent({ templatePath });
			setSelectedTemplate(result.data.template);
			setSelectedTemplatePath(result.data.template.templatePath);
			setTemplateContent(result.data.content);
			setTemplateMeta({
				cached: result.data.cached,
				fetchedAt: result.data.fetchedAt,
				expiresAt: result.data.expiresAt,
			});
			const existing = existingOverrides.find(
				(override) =>
					override.templatePath === result.data.template.templatePath,
			);
			setActiveOverrideId(existing?.id || result.data.template.templateId);
			if (existing?.content) {
				setTemplateContent(existing.content);
			}
		} catch (error) {
			console.error(error);
			toast.error("Failed to load template content.");
		} finally {
			setIsLoadingContent(false);
		}
	};

	const handleSelectExistingOverride = async (
		override: AgentTemplateOverride,
	) => {
		setActiveTemplateTab("editor");
		setSelectedTemplatePath(override.templatePath);
		setActiveOverrideId(override.id || override.templateId);
		setTemplateContent(override.content);
		setTemplateMeta(null);
		const templateFromCatalog = availableTemplates.find(
			(template) => template.templatePath === override.templatePath,
		);
		setSelectedTemplate(
			templateFromCatalog || {
				templateId: override.templateId,
				templatePath: override.templatePath,
				backend: override.backend,
				sourcePath:
					override.backend === "agent_generator"
						? `services/functions/src/templates/${override.templatePath.replace(
								/^agent_generator\//,
								"",
							)}`
						: `agents/agentc/src/agentc/templates/${override.templatePath}`,
				sha: override.checksum || "",
			},
		);
		if (!templateFromCatalog) {
			await handleLoadTemplate(override.templatePath);
		}
	};

	const handleSaveOverride = async () => {
		if (!selectedTemplate) {
			toast.error("Select a template before saving an override.");
			return;
		}
		setIsSavingOverride(true);
		try {
			const overrideId = activeOverrideId || selectedTemplate.templateId;
			await upsertTemplateOverride(
				db,
				agent.companyId,
				agent.id || "",
				overrideId,
				{
					templateId: selectedTemplate.templateId,
					templatePath: selectedTemplate.templatePath,
					backend: selectedTemplate.backend,
					content: templateContent,
					enabled: true,
					updatedBy: user?.email || user?.uid || "admin",
					checksum: selectedTemplate.sha,
				},
			);
			await loadOverrides();
			setActiveOverrideId(overrideId);
			toast.success("Template override saved.");
		} catch (error) {
			console.error(error);
			toast.error("Failed to save template override.");
		} finally {
			setIsSavingOverride(false);
		}
	};

	const clearTemplateEditor = () => {
		setActiveOverrideId(null);
		setSelectedTemplate(null);
		setSelectedTemplatePath("");
		setTemplateContent("");
		setTemplateMeta(null);
	};

	const handleDeleteOverride = async (overrideId?: string) => {
		const targetOverrideId = overrideId ?? activeOverrideId;
		if (!targetOverrideId) {
			toast.error("No override selected.");
			return;
		}
		try {
			setDeletingOverrideId(targetOverrideId);
			await deleteTemplateOverride(
				db,
				agent.companyId,
				agent.id || "",
				targetOverrideId,
			);
			await loadOverrides();
			if (activeOverrideId === targetOverrideId) {
				clearTemplateEditor();
			}
			setActiveTemplateTab("overrides");
			toast.success("Template override deleted.");
		} catch (error) {
			console.error(error);
			toast.error("Failed to delete template override.");
		} finally {
			setDeletingOverrideId(null);
		}
	};

	return (
		<>
			<div className="space-y-6">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-4">
						<Button variant="ghost" onClick={onNavigateBack}>
							<ArrowLeft className="mr-2 h-4 w-4" />
							Back to Agents
						</Button>
					</div>
					<div className="flex gap-2">
						{isEditing ? (
							<>
								<Button variant="outline" onClick={handleCancel}>
									Cancel
								</Button>
								<Button onClick={() => void handleSave()}>Save Changes</Button>
							</>
						) : (
							<>
								<Button
									variant="destructive"
									size="sm"
									onClick={() => setIsDeleteDialogOpen(true)}
								>
									<Trash2 className="h-4 w-4 mr-2" />
									Delete Agent
								</Button>
								<Button onClick={() => setIsEditing(true)}>Edit Agent</Button>
							</>
						)}
					</div>
				</div>

				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<CardTitle>Agent Information</CardTitle>
							{getStatusBadge(agent.status)}
						</div>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<Label>Agent ID</Label>
								<Input
									value={agent.id}
									disabled
									className="mt-1 bg-slate-100"
								/>
							</div>
							<div>
								<Label>Phone Numbers</Label>
								<Input
									value={agent.phoneNumbers.join(", ")}
									disabled
									className="mt-1 bg-slate-100"
								/>
							</div>
							<div>
								<Label>Status</Label>
								<Select
									value={isEditing ? formData.status : agent.status}
									onValueChange={(value: AgentStatus) =>
										setFormData({ ...formData, status: value })
									}
									disabled={!isEditing}
								>
									<SelectTrigger className="mt-1">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="Active">Active</SelectItem>
										<SelectItem value="Draft">Draft</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label>Inbound answering</Label>
								<Select
									value={
										isEditing
											? formData.inboundAnsweringPolicy
											: getAgentInboundAnsweringPolicy(agent)
									}
									onValueChange={(value: InboundAnsweringPolicy) =>
										setFormData({ ...formData, inboundAnsweringPolicy: value })
									}
									disabled={!isEditing}
								>
									<SelectTrigger className="mt-1">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="AI_FIRST">AI first</SelectItem>
										<SelectItem value="AI_ONLY">AI only</SelectItem>
										<SelectItem value="HUMAN_FIRST">Human first</SelectItem>
										<SelectItem value="HUMAN_ONLY">Human only</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label>Company</Label>
								<Select
									value={isEditing ? formData.companyId : agent.companyId}
									onValueChange={(value) =>
										setFormData({ ...formData, companyId: value })
									}
									disabled={!isEditing}
								>
									<SelectTrigger className="mt-1">
										<SelectValue
											placeholder={company?.name || "Select a company"}
										/>
									</SelectTrigger>
									<SelectContent>
										{companies.map((c) => (
											<SelectItem key={c.id} value={c.id}>
												{c.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label>Service</Label>
								<Select
									value={
										isEditing ? formData.serviceId : agent.serviceId || "none"
									}
									onValueChange={(value) =>
										setFormData({ ...formData, serviceId: value })
									}
									disabled={!isEditing}
								>
									<SelectTrigger className="mt-1">
										<SelectValue
											placeholder={service?.name || "No service assigned"}
										/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="none">No service</SelectItem>
										{services.map((s) => (
											<SelectItem key={s.id} value={s.id}>
												{s.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label>Created</Label>
								<Input
									value={formatTimestamp(agent.createdAt as string | null)}
									disabled
									className="mt-1 bg-slate-100"
								/>
							</div>
							<div>
								<Label>Updated</Label>
								<Input
									value={formatTimestamp(agent.updatedAt as string | null)}
									disabled
									className="mt-1 bg-slate-100"
								/>
							</div>
						</div>
					</CardContent>
				</Card>

				<AdminCallStats stats={agentStats} />

				<OutboundPlaybooksPanel agent={agent} />

				<Card>
					<CardHeader>
						<div className="flex items-center justify-between gap-4">
							<div>
								<CardTitle>Template Overrides</CardTitle>
								<p className="text-sm text-slate-500">
									Load an AgentC or agent-generator template, edit it, and save
									an agent-specific override.
								</p>
							</div>
							<Button
								variant="outline"
								onClick={() => void loadTemplateCatalog()}
								disabled={isLoadingTemplates}
							>
								{isLoadingTemplates ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<RefreshCw className="mr-2 h-4 w-4" />
								)}
								Refresh template list
							</Button>
						</div>
					</CardHeader>
					<CardContent>
						<Tabs
							value={activeTemplateTab}
							onValueChange={setActiveTemplateTab}
							className="w-full"
						>
							<TabsList className="grid w-full grid-cols-2">
								<TabsTrigger value="editor">Editor</TabsTrigger>
								<TabsTrigger value="overrides">
									Existing Overrides ({existingOverrides.length})
								</TabsTrigger>
							</TabsList>
							<TabsContent value="editor" className="mt-6 space-y-4">
								<div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
									<div>
										<Label>Template file</Label>
										<Select
											value={selectedTemplatePath}
											onValueChange={setSelectedTemplatePath}
										>
											<SelectTrigger className="mt-1">
												<SelectValue placeholder="Choose a template" />
											</SelectTrigger>
											<SelectContent>
												{availableTemplates.map((template) => (
													<SelectItem
														key={template.templatePath}
														value={template.templatePath}
													>
														{template.templatePath}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
									<Button
										onClick={() => void handleLoadTemplate()}
										disabled={isLoadingContent}
									>
										{isLoadingContent ? (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										) : null}
										Load template
									</Button>
								</div>

								{selectedTemplate ? (
									<div className="space-y-4 rounded-lg border p-4">
										<div className="flex flex-wrap items-center gap-2">
											<Badge variant="outline">
												{selectedTemplate.backend}
											</Badge>
											<Badge variant="secondary">
												{selectedTemplate.templatePath}
											</Badge>
											{templateMeta ? (
												<Badge variant="outline">
													{templateMeta.cached
														? "Loaded from cache"
														: "Loaded from GitHub"}
												</Badge>
											) : null}
										</div>
										{templateMeta ? (
											<p className="text-xs text-slate-500">
												Fetched{" "}
												{new Date(templateMeta.fetchedAt).toLocaleString()} ·
												cache expires{" "}
												{new Date(templateMeta.expiresAt).toLocaleString()}
											</p>
										) : null}
										<div>
											<Label>Override content</Label>
											<Textarea
												value={templateContent}
												onChange={(event) =>
													setTemplateContent(event.target.value)
												}
												className="mt-1 min-h-[420px] font-mono text-xs"
											/>
										</div>
										<div className="flex flex-wrap gap-2">
											<Button
												onClick={() => void handleSaveOverride()}
												disabled={isSavingOverride}
											>
												{isSavingOverride ? (
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												) : null}
												Save override
											</Button>
											<Button
												variant="outline"
												onClick={() =>
													void handleLoadTemplate(selectedTemplate.templatePath)
												}
											>
												Reset to upstream
											</Button>
											<Button
												variant="destructive"
												onClick={() => void handleDeleteOverride()}
												disabled={
													!activeOverrideId || deletingOverrideId !== null
												}
											>
												{deletingOverrideId === activeOverrideId ? (
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												) : null}
												Delete override
											</Button>
										</div>
									</div>
								) : (
									<div className="rounded-lg border border-dashed p-8 text-center text-sm text-slate-500">
										Choose a template file and load its content to begin
										editing.
									</div>
								)}
							</TabsContent>
							<TabsContent value="overrides" className="mt-6 space-y-4">
								{existingOverrides.length === 0 ? (
									<div className="rounded-lg border border-dashed p-8 text-center text-sm text-slate-500">
										No template overrides saved for this agent yet.
									</div>
								) : (
									<div className="space-y-3">
										{existingOverrides.map((override) => (
											<div
												key={override.id || override.templateId}
												className="flex items-center justify-between rounded-lg border p-3"
											>
												<div>
													<div className="font-medium">
														{override.templatePath}
													</div>
													<div className="text-xs text-slate-500">
														{override.backend} · updated{" "}
														{formatTimestamp(
															override.updatedAt as string | null,
														)}
													</div>
												</div>
												<div className="flex gap-2">
													<Button
														variant="outline"
														onClick={() =>
															void handleSelectExistingOverride(override)
														}
													>
														Open in editor
													</Button>
													<Button
														variant="destructive"
														onClick={() =>
															void handleDeleteOverride(
																override.id || override.templateId,
															)
														}
														disabled={deletingOverrideId !== null}
													>
														{deletingOverrideId ===
														(override.id || override.templateId) ? (
															<Loader2 className="mr-2 h-4 w-4 animate-spin" />
														) : null}
														Delete
													</Button>
												</div>
											</div>
										))}
									</div>
								)}
							</TabsContent>
						</Tabs>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>Assigned Phone Numbers</CardTitle>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Number</TableHead>
									<TableHead>Provider</TableHead>
									<TableHead>Last Call</TableHead>
									<TableHead>Monthly Cost</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{agentPhoneNumbers.map((phone) => (
									<TableRow key={phone.id}>
										<TableCell className="font-mono">{phone.number}</TableCell>
										<TableCell>
											<Badge variant="outline">{phone.provider}</Badge>
										</TableCell>
										<TableCell>
											{formatTimestamp(phone.lastCallAt as string | null)}
										</TableCell>
										<TableCell>
											${((phone.monthlyCostInCents || 0) / 100).toFixed(2)}/mo
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>Recent Calls</CardTitle>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Call SID</TableHead>
									<TableHead>From</TableHead>
									<TableHead>To</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Duration</TableHead>
									<TableHead>Date</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{recentCalls.length > 0 ? (
									recentCalls.map((call) => (
										<TableRow key={call.sid}>
											<TableCell className="font-mono text-xs">
												{call.sid}
											</TableCell>
											<TableCell>{call.from}</TableCell>
											<TableCell>{call.to}</TableCell>
											<TableCell>
												{getCallStatusBadge(call.status ?? "")}
											</TableCell>
											<TableCell>
												{formatDuration(call.duration ?? "")}
											</TableCell>
											<TableCell>
												{formatTimestamp(call.dateCreated ?? null)}
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell
											colSpan={6}
											className="text-center text-slate-500"
										>
											No recent calls found for this agent
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			</div>

			<TypedDeleteConfirmationDialog
				open={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
				entityType="Agent"
				entityName={agent?.name ?? ""}
				consequences="This action cannot be undone. Any assigned phone numbers will become unassigned."
				onConfirm={handleDeleteAgent}
				isDeleting={isDeleting}
			/>
		</>
	);
}
